<template>
  <div>
    <request-info-form
      v-if="showForm"
      v-model="formData"
      :step-defs="stepDefinitions"
      @onComplete="onComplete"
    >
      <template slot="step3_footer">
        <vs-row v-if="reviewData.items">
          <div class="vx-col w-full md:w-1/4 mb-base">
            <vs-row class="font-semibold">{{ $t('users.first_name') }}</vs-row>
            <vs-row class="mt-3">{{ reviewData.firstName }}</vs-row>
          </div>
          <div class="vx-col w-full md:w-1/4 mb-base">
            <vs-row class="font-semibold">{{ $t('users.last_name') }}</vs-row>
            <vs-row class="mt-3">{{ reviewData.lastName }}</vs-row>
          </div>
          <div class="vx-col w-full md:w-1/4 mb-base">
            <vs-row class="font-semibold">{{ $t('users.genders.gender') }}</vs-row>
            <vs-row class="mt-3">{{ $t(`users.genders.${reviewData.gender}`) }}</vs-row>
          </div>
          <div class="vx-col w-full md:w-1/4 mb-base">
            <vs-row class="font-semibold">{{ $t('request_forms.age') }}</vs-row>
            <vs-row class="mt-3">{{ reviewData.age | persian_digits }}</vs-row>
          </div>
          <div class="vx-col w-full md:w-1/3 mb-base">
            <vs-row class="font-semibold">{{ $t('users.phone') }}</vs-row>
            <vs-row class="mt-3">{{ reviewData.phoneNumber | persian_digits }}</vs-row>
          </div>
          <div class="vx-col w-full md:w-1/3 mb-base">
            <vs-row class="font-semibold">{{ $t('users.emergency_phone') }}</vs-row>
            <vs-row class="mt-3">{{ reviewData.emergencyNumber | persian_digits }}</vs-row>
          </div>
          <div class="vx-col w-full md:w-1/3 mb-base">
            <vs-row class="font-semibold">{{ $t('request_forms.locale') }}</vs-row>
            <vs-row class="mt-3">{{ reviewData.locale || $t('general.persian') }}</vs-row>
          </div>
          <div class="vx-col w-full md:w-1/2 mb-base">
            <vs-row class="font-semibold">{{ $t('request_forms.for_when') }}</vs-row>
            <vs-row class="mt-3" v-if="reviewData.date">{{ reviewData.date | datetime }}</vs-row>
            <vs-row class="mt-3" v-else>{{ $t('request_forms.for_now') }}</vs-row>
          </div>
          <div class="vx-col w-full mb-base">
            <vs-row class="font-semibold">{{ $t('request_forms.services') }}</vs-row>
            <vs-row class="mt-3">
              <vs-table ref="table" :data="reviewData.items" class="w-full">
                <template slot="thead">
                  <vs-th>{{ $t('request_forms.service_name') }}</vs-th>
                  <vs-th>{{ $t('request_forms.price') }} </vs-th>
                  <vs-th>{{ $t('request_forms.number_of_service') }} </vs-th>
                  <vs-th>{{ $t('request_forms.total_price') }}</vs-th>
                </template>
                <template slot-scope="{ data }">
                  <tbody v-if="data.length">
                    <vs-tr :data="tr" :key="indextr" v-for="(tr, indextr) in data">
                      <vs-td>
                        {{ $t(`health_services.licensing.${tr.service}`) }}
                      </vs-td>
                      <vs-td>
                        {{ services[tr.service].price | persian_digits }}
                      </vs-td>
                      <vs-td> {{ tr.count | persian_digits }} {{ $t(`units.${tr.unit}`) }} </vs-td>
                      <vs-td>
                        {{ (services[tr.service].price * tr.count) | persian_digits }}
                      </vs-td>
                    </vs-tr>
                  </tbody>
                  <tfoot>
                    <vs-tr class="font-semibold">
                      <vs-td></vs-td>
                      <vs-td></vs-td>
                      <vs-td class="text-right">{{ $t('invoices.total') }}:</vs-td>
                      <vs-td>{{
                        reviewData.items
                          ? reviewData.items.reduce(
                              (accum, item) => accum + services[item.service].price * item.count,
                              0
                            )
                          : 0 | persian_digits
                      }}</vs-td>
                    </vs-tr>
                  </tfoot>
                </template>
              </vs-table>
            </vs-row>
          </div>

          <download-files class="mb-base" :files="reviewData.attachments" />

          <div class="vx-col w-full mb-base" v-if="reviewData.description">
            <vs-row class="font-semibold">{{ $t('documents.description') }}</vs-row>
            <vs-row class="mt-3">{{ reviewData.description }}</vs-row>
          </div>
          <div class="text-warning mb-base font-semibold text-lg">
            <i class="fa fa-exclamation-triangle" /> {{ $t('request_forms.pre_invoice_alert') }}
          </div>
          <div class="vx-col w-full mb-base">
            <vs-row class="font-semibold">{{ $t('users.addresses.address') }}</vs-row>
            <vs-row class="mt-3">
              <address-string v-if="reviewData.address" :address="reviewData.address" hide-label />
            </vs-row>
            <open-street-map
              :markers="[
                { latlng: { lat: reviewData.address.location.lat, lng: reviewData.address.location.lon } }
              ]"
              :zoom="13"
              disabled
              view-only
            />
            <vs-row
              vs-justify="flex-end"
              class="mt-base"
              v-if="$acl.check(['manager']) && reviewData.type === 'other'"
            >
              <vs-button color="danger" @click="createCaseManager">
                {{ $t('request_forms.create_as_case_manager') }}
              </vs-button>
            </vs-row>
          </div>
        </vs-row>
      </template>
      <template slot="select_suggestion_step_footer">
        <select-suggestion
          v-if="selectedSuggestionView && reviewData.address"
          v-model="formData.prefer"
          subject="licensing"
          category="tourism"
          :address="reviewData.address"
          :locale="reviewData.locale"
          :date="reviewData.forWhen === 'now' ? undefined : reviewData.date"
        />
      </template>
    </request-info-form>
  </div>
</template>

<script>
import SelectSuggestion from '@/views/components/forms/SelectSuggestion'
import RequestInfoForm from '@/views/components/forms/RequestInfoForm'
import AddressString from '@/views/components/maps/AddressString'
import OpenStreetMap from '@/views/components/maps/OpenStreetMap'
import DownloadFiles from '@/views/components/forms/DownloadFiles'

export default {
  name: 'licensing-request',
  components: {
    SelectSuggestion,
    RequestInfoForm,
    DownloadFiles,
    OpenStreetMap,
    AddressString
  },
  data() {
    return {
      showForm: true,
      formData: {
        forWhom: 1
      },
      services: [],
      selectedSuggestionView: false,
      createAsCaseManager: false
    }
  },
  computed: {
    stepDefinitions() {
      let steps = {
        step1: {
          title: this.$t('request_forms.step_one'),
          fields: {
            forWhom: {
              initValue: this.formData.forWhom,
              type: 'radio',
              label: this.$t('forms.select_option'),
              icon: 'icon-user',
              iconPack: 'feather',
              validator: 'required',
              styleClasses: 'w-full md:w-1/2',
              options: [
                { label: this.$t('request_forms.for_myself'), value: 1 },
                { label: this.$t('request_forms.for_another'), value: 2 }
              ]
            },
            firstName: {
              type: 'input',
              inputType: 'text',
              label: this.$t('users.first_name'),
              iconPack: 'feather',
              validator: 'required|letters',
              styleClasses: 'w-full md:w-1/2'
            },
            lastName: {
              type: 'input',
              inputType: 'text',
              label: this.$t('users.last_name'),
              iconPack: 'feather',
              validator: 'required|letters',
              styleClasses: 'w-full md:w-1/2'
            },
            gender: {
              type: 'select',
              label: this.$t('users.genders.gender'),
              validator: 'required',
              styleClasses: 'w-full md:w-1/2',
              clearable: false,
              options: [
                { label: this.$t('users.genders.male'), value: 'male' },
                { label: this.$t('users.genders.female'), value: 'female' },
                { label: this.$t('users.genders.other'), value: 'other' }
              ]
            },
            age: {
              type: 'input',
              inputType: 'number',
              label: this.$t('request_forms.age'),
              validator: 'required',
              styleClasses: 'w-full md:w-1/2'
            },
            phoneNumber: {
              type: 'input',
              inputType: 'text',
              label: this.$t('users.phone'),
              icon: 'icon-phone',
              iconPack: 'feather',
              validator: 'required|phone:any',
              styleClasses: 'w-full md:w-1/2'
            },
            emergencyNumber: {
              type: 'input',
              inputType: 'text',
              label: this.$t('users.emergency_phone'),
              icon: 'icon-phone',
              iconPack: 'feather',
              validator: 'required|phone:any',
              styleClasses: 'w-full md:w-1/2'
            },
            locale: {
              type: 'select',
              inputType: 'text',
              label: this.$t('request_forms.locale'),
              validator: 'required',
              clearable: false,
              initValue: 'fa',
              options: [
                { label: this.$t('general.persian'), value: 'fa' },
                { label: this.$t('general.english'), value: 'en' },
                { label: this.$t('general.arabic'), value: 'ar' }
              ],
              styleClasses: 'w-full md:w-1/2'
            },
            forWhen: {
              type: 'select',
              label: this.$t('request_forms.for_when'),
              validator: 'required',
              clearable: false,
              initValue: 'now',
              options: [
                { label: this.$t('request_forms.for_now'), value: 'now' },
                { label: this.$t('request_forms.for_later'), value: 'later' }
              ],
              styleClasses: 'w-full md:w-1/4'
            },
            date: {
              type: 'datetime',
              inputType: 'datetime',
              label: this.$t('forms.time'),
              icon: 'icon-clock',
              iconPack: 'feather',
              initValue: this.$moment()
                .add(24, 'hour')
                .format(),
              min: this.$moment()
                .add(24, 'hour')
                .format(),
              max: this.$moment()
                .add(2, 'week')
                .endOf('day')
                .format(),
              styleClasses: 'w-full md:w-1/4'
            },
            items: {
              type: 'select_services',
              label: this.$t('request_forms.services'),
              subject: 'licensing',
              validator: 'required',
              styleClasses: 'w-full'
            }
          }
        },
        step2: {
          title: this.$t('request_forms.step_two'),
          fields: {
            address: {
              type: 'select_address',
              label: this.$t('users.addresses.address'),
              validator: 'required',
              styleClasses: 'w-full',
              icon: 'icon-map-pin',
              iconPack: 'feather'
            },
            attachments: {
              type: 'upload_file',
              styleClasses: 'w-full',
              multiple: true,
              uploadAuto: true
            },
            description: {
              type: 'textarea',
              label: this.$t('documents.description'),
              styleClasses: 'w-full',
              placeholder: this.$t('request_forms.description_placeholder')
            }
          }
        },
        select_suggestion_step: {
          title: this.$t('request_forms.step_three'),
          fields: {}
        },
        step3: {
          title: this.$t('request_forms.step_four'),
          fields: {}
        }
      }
      if (this.formData.forWhom === 1) {
        delete steps.step1.fields.firstName
        delete steps.step1.fields.lastName
        delete steps.step1.fields.gender
        delete steps.step1.fields.age
        steps.step1.fields.phoneNumber.initValue = this.$store.state.AppActiveUser.username
        steps.step1.fields.emergencyNumber.initValue = ''
      }
      if (this.formData.forWhom === 2) {
        steps.step1.fields.phoneNumber.initValue = ''
        steps.step1.fields.emergencyNumber.initValue = this.$store.state.AppActiveUser.username
      }
      if (this.formData.forWhen === 'now') {
        delete steps.step1.fields.date
        steps.step1.fields.forWhen.styleClasses = 'w-full md:w-1/2'
      }
      return steps
    },
    reviewData() {
      let data = Object.assign({}, this.formData)
      if (data.forWhom === 1) {
        data.firstName = this.$store.state.AppActiveUser.details.firstName
        data.lastName = this.$store.state.AppActiveUser.details.lastName
        data.gender = this.$store.state.AppActiveUser.details.gender
        data.age = this.$moment().diff(
          this.$moment(this.$store.state.AppActiveUser.details.birthday),
          'years'
        )
        data.type = 'own'
      } else {
        data.age = parseInt(data.age)
        data.type = 'other'
      }
      if (data.forWhen === 'now') {
        delete data.date
      }
      if (data.locale === 'fa') {
        delete data.locale
      }
      data.createAsCaseManager = this.createAsCaseManager
      data.address = this.$store.state.AppActiveUser.addresses[data.address]
        ? this.$store.state.AppActiveUser.addresses[data.address]
        : { location: { lat: 0, lon: 0 } }
      delete data.forWhom
      delete data.forWhen
      delete data._id
      if (data.address) {
        delete data.address._id
        if (data.address.location) delete data.address.location._id
      }
      if (data.prefer) {
        data.preferredPlaces = data.prefer.preferredPlaces
        data.preferredServices = data.prefer.preferredServices
        delete data.prefer
      }
      return data
    }
  },
  watch: {
    'formData.forWhom'() {
      this.showForm = false
      this.$nextTick(() => (this.showForm = true))
    },
    'formData.locale'() {
      if (this.formData.address != null) {
        this.selectedSuggestionView = false
        this.$nextTick(() => {
          this.selectedSuggestionView = true
        })
      }
    },
    'formData.address'() {
      this.selectedSuggestionView = false
      this.$nextTick(() => {
        this.selectedSuggestionView = true
      })
    }
  },
  created() {
    const serviceDetails = this.$store.getters['config/provinceServiceDetails']
    this.services = serviceDetails.licensing
  },
  methods: {
    onComplete() {
      this.$vs.loading()
      this.$store
        .dispatch('request/createRequestBySubject', {
          category: 'tourism',
          subject: 'licensing',
          data: this.reviewData
        })
        .then(res => {
          this.$vs.notify({
            color: 'success',
            title: this.$t('request_forms.successfully_created')
          })
          this.$router.push({ name: 'rendered-service-view', params: { id: res.data.id } })
        })
        .finally(() => this.$vs.loading.close())
    },
    createCaseManager() {
      this.createAsCaseManager = true
      this.onComplete()
    }
  }
}
</script>
